import { subscribeCicdEvents } from "../../../hooks/useCicdEvents";
import { CicdMonitorButton } from "../../../components/CicdExecution";
import React, { useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress } from "@mui/material";
import { api } from "../../../utils/helpers";
import { stageLabel } from "../../../utils/stages";
import { useLang } from "../i18n";

const states = new Set(["waiting_cloud", "verifying_cloud", "syncing_edge", "completed", "failed", "unknown", "retired"]);
export function edgeFollowupStatus(jobs) {
  const rows = jobs.flatMap(job => Array.isArray(job.pids) ? job.pids : []);
  if (jobs.some(job => job.status === "failed") || rows.some(row => row.status === "failed")) return "failed";
  if (jobs.some(job => job.status === "retired")) return "retired";
  if (jobs.some(job => job.status === "unknown") || rows.some(row => row.status === "unknown")) return "unknown";
  if (jobs.length && jobs.every(job => job.status === "completed") && rows.length && rows.every(row => row.status === "completed")) return "completed";
  if (jobs.some(job => job.status === "syncing_edge")) return "syncing_edge";
  if (jobs.some(job => job.status === "verifying_cloud")) return "verifying_cloud";
  return "waiting_cloud";
}

export function BatchEdgeStatus({ spaceId, sourceSha, targetStage, enabled }) {
  const { lang } = useLang();
  const key = `${spaceId}:${sourceSha}:${targetStage}:${enabled}:${lang}`;
  const [result, setResult] = useState({ key: "", value: null });
  useEffect(() => {
    if (!enabled || !sourceSha || !spaceId) return;
    let active = true, loading = false;
    const controller = new AbortController();
    const poll = async () => {
      if (!active || loading) return; loading = true;
      try {
        const response = await api(`/api/cicd/transport/edge-followups?spaceId=${encodeURIComponent(spaceId)}&sourceSha=${encodeURIComponent(sourceSha)}&language=${lang}`, { signal: controller.signal });
        if (!Array.isArray(response?.jobs)) throw new Error("EDGE_REQUEST_FAILED");
        if (!active) return;
        const jobs = response.jobs.filter(job => job.targetStage === targetStage);
        const status = edgeFollowupStatus(jobs);
        setResult({ key, value: { jobs, status } });

      } catch {
        if (!active) return;
        setResult({ key, value: { status: "unknown", readError: true } });
      } finally { loading = false; }
    };
    const initialRead = poll();
    const close = subscribeCicdEvents(spaceId, async () => {
      await initialRead;
      if (active) await poll();
    });
    return () => { active = false; controller.abort(); close(); };
  }, [key, spaceId, sourceSha, targetStage, enabled, lang]);
  if (!enabled || !sourceSha) return null;
  return <ReviewEdgeStatus spaceId={spaceId} value={result.key === key ? result.value : { status: "waiting_cloud", checking: true }} />;
}

export default function ReviewEdgeStatus({ value, spaceId }) {
  const { t } = useLang();
  if (!value) return null;
  const status = states.has(value.status) ? value.status : "unknown";
  return <Alert className="sect" severity={status === "completed" ? "success" : ["failed", "unknown"].includes(status) ? "warning" : "info"}>
    <strong>{t("pdEdge.followupTitle")}</strong>
    {value.checking && status !== "retired" && <CircularProgress size={16} sx={{ ml: 1 }} aria-label={t("pdEdge.loading")} />}
    <div>{t(value.readError ? "pdEdge.statusReadFailed" : `pdEdge.status.${status}`)}</div>
    {(value.jobs || []).map(job => <Box key={job.id} sx={{ mt: 1 }}>
      <span>{job.targetInstanceName || stageLabel(job.targetStage)}</span>
      {job.error?.message && <div>{job.error.message}</div>}
      {(job.pids || []).map(row => <Box key={`${row.pid}:${row.runtimeId}`} sx={{ display: "flex", gap: 1, flexWrap: "wrap", overflowWrap: "anywhere" }}>
        <span>{row.pid} · {row.runtimeId}</span>
        <span>{t(`pdEdge.status.${job.status === "retired" ? "retired" : states.has(row.status) ? row.status : "unknown"}`)}</span>
        {["failed", "unknown"].includes(row.status) && <CicdMonitorButton spaceId={spaceId} runId={row.edgeRunId} operationId={job.operationId} execution={row.execution}/>}
        {/^https:\/\//.test(row.edgeRunUrl || "") && <Button size="small" component="a" href={row.edgeRunUrl} target="_blank" rel="noopener noreferrer">{t("batch.run")}</Button>}
      </Box>)}
    </Box>)}
  </Alert>;
}
